import {
  BasisBadge,
  BasisDivergenceNote,
} from "@app/components/basis-switcher";
import KpiChart from "@app/components/kpi-chart";
import { basisParam, useBasis } from "@app/lib/basis";
import { dateRangeFromISO, useDateRange } from "@app/lib/date-range";
import { useMemo, useState } from "react";

import CacMonthPanel from "@app/pages/cac/cac-month-panel";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { cacPage_Query } from "@app/queries/cacPage_Query.graphql";

export const cacQuery = graphql`
  query cacPage_Query($from: String, $basis: BasisEnum) {
    unitEconomics {
      ltv
      arpu
      grossMarginTtm
    }
    fullyLoadedCac(from: $from, basis: $basis) {
      months
      newCustomers {
        month
        count
      }
      components {
        key
        label
        pnlLine
        defaultIncluded
        categoryCount
        monthly {
          month
          spend
        }
      }
    }
  }
`;

type Component = cacPage_Query["response"]["fullyLoadedCac"]["components"][number];

// Per-cost-centre overrides on top of the backend defaults (included from
// `defaultIncluded`, proration 100%). proration is 0..1.
type Override = { included?: boolean; proration?: number };

// Cost centres are dynamic (one per S&M category + optional others), so colours
// are assigned by position rather than keyed to fixed component names.
const PALETTE = [
  "#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#db2777",
  "#ea580c", "#0d9488", "#9333ea", "#ca8a04", "#dc2626",
  "#4f46e5", "#059669", "#c026d3", "#0284c7", "#65a30d",
];

const money = (value: number | null) =>
  value == null
    ? "—"
    : value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

// Trailing-12 sum ending at index i (uses up to 12 prior entries of a
// month-ordered array), null when the window has no customers.
function ttmRatio(
  loaded: number[],
  customers: number[],
  i: number
): number | null {
  const start = Math.max(0, i - 11);
  let cost = 0;
  let count = 0;
  for (let j = start; j <= i; j++) {
    cost += loaded[j];
    count += customers[j];
  }
  return count > 0 ? cost / count : null;
}

export default function CacPage() {
  // The header date range bounds the monthly series; the TTM CAC / LTV:CAC
  // headlines stay trailing-12 (computed client-side from the latest months).
  const from = dateRangeFromISO(useDateRange());
  const basis = basisParam(useBasis());
  const data = useLazyLoadQuery<cacPage_Query>(cacQuery, {
    from: from ?? undefined,
    basis: basis ?? undefined,
  });
  const flc = data.fullyLoadedCac;
  const econ = data.unitEconomics;

  const [overrides, setOverrides] = useState<Record<string, Override>>({});
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const months = flc.months;
  const components = flc.components;

  // Effective config per component: backend default unless the user overrode it.
  const config = useMemo(() => {
    const map: Record<string, { included: boolean; proration: number }> = {};
    components.forEach((c) => {
      const o = overrides[c.key] ?? {};
      map[c.key] = {
        included: o.included ?? c.defaultIncluded,
        proration:
          typeof o.proration === "number" && Number.isFinite(o.proration)
            ? o.proration
            : 1,
      };
    });
    return map;
  }, [components, overrides]);

  const colorByKey = useMemo(() => {
    const map: Record<string, string> = {};
    components.forEach((c, i) => {
      map[c.key] = PALETTE[i % PALETTE.length];
    });
    return map;
  }, [components]);

  // key -> (month -> spend dollars)
  const spendByComp = useMemo(() => {
    const map: Record<string, Map<string, number>> = {};
    components.forEach((c) => {
      map[c.key] = new Map(c.monthly.map((p) => [p.month, p.spend]));
    });
    return map;
  }, [components]);

  const newByMonth = useMemo(
    () => new Map(flc.newCustomers.map((p) => [p.month, p.count])),
    [flc.newCustomers]
  );

  // S&M cost centres are shown by default; non-S&M categories with spend are
  // offered as optional additions.
  const smComponents = components.filter((c) => c.defaultIncluded);
  const otherComponents = components.filter(
    (c) => !c.defaultIncluded && c.monthly.length > 0
  );

  // Loaded cost per month under the current include/proration config.
  const loadedCost = months.map((month) =>
    components.reduce((sum, c) => {
      if (!config[c.key].included) return sum;
      const spend = spendByComp[c.key]?.get(month) ?? 0;
      return sum + spend * config[c.key].proration;
    }, 0)
  );

  const customers = months.map((m) => newByMonth.get(m) ?? 0);
  const monthlyCac = months.map((_, i) =>
    customers[i] > 0 ? loadedCost[i] / customers[i] : null
  );
  const ttmCac = months.map((_, i) => ttmRatio(loadedCost, customers, i));

  const currentCac = [...ttmCac].reverse().find((v) => v != null) ?? null;
  const lastMonthCac = [...monthlyCac].reverse().find((v) => v != null) ?? null;

  const ltvToCac =
    currentCac && currentCac > 0 && econ.ltv != null
      ? econ.ltv / currentCac
      : null;
  const paybackMonths =
    currentCac &&
    econ.arpu != null &&
    econ.grossMarginTtm != null &&
    econ.arpu * econ.grossMarginTtm > 0
      ? currentCac / (econ.arpu * econ.grossMarginTtm)
      : null;

  const setIncluded = (key: string, included: boolean) =>
    setOverrides((prev) => ({ ...prev, [key]: { ...prev[key], included } }));

  const setProration = (key: string, percent: number) =>
    setOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        proration: Math.max(0, Math.min(100, percent)) / 100,
      },
    }));

  const includedKeys = components
    .filter((c) => config[c.key].included)
    .map((c) => c.key);

  const spendSeries = components
    .filter((c) => config[c.key].included)
    .map((c) => ({
      name: c.label,
      type: "bar" as const,
      stack: "spend",
      color: colorByKey[c.key],
      points: months.map((month) => ({
        month,
        value: (spendByComp[c.key]?.get(month) ?? 0) * config[c.key].proration,
      })),
    }));

  // Data-health always covers the S&M cost centres (so gaps show even when a
  // user temporarily unchecks one) plus any other category folded in.
  const shownComponents = components.filter(
    (c) => c.defaultIncluded || config[c.key].included
  );

  if (months.length === 0) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-5">
        <h1 className="text-xl font-bold">Fully Loaded CAC</h1>
        <p className="text-gray-500">
          No cost or customer data yet. Categorize transactions and sync Stripe.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold">Fully Loaded CAC</h1>
          <BasisBadge />
        </div>
        <p className="text-sm text-gray-500">
          Acquisition cost per new customer. Cost centres are your Sales &
          Marketing categories and their subcategories; prorate each or fold in
          other categories to see CAC under different definitions of "fully
          loaded". New customers = new paying logos from Stripe.
        </p>
      </div>

      {/* Headline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="TTM CAC" value={money(currentCac)} primary />
        <Stat label="Latest-month CAC" value={money(lastMonthCac)} />
        <Stat
          label="LTV : CAC"
          value={ltvToCac == null ? "—" : `${ltvToCac.toFixed(1)}×`}
        />
        <Stat
          label="CAC payback"
          value={paybackMonths == null ? "—" : `${paybackMonths.toFixed(1)} mo`}
        />
      </div>

      {/* Definition controls */}
      <div className="space-y-4 bg-white rounded border p-4">
        <CostCentreGroup
          title="Sales & Marketing cost centres"
          subtitle="Tagged S&M (and their subcategories) — included by default."
          components={smComponents}
          config={config}
          colorByKey={colorByKey}
          onIncluded={setIncluded}
          onProration={setProration}
        />
        {otherComponents.length > 0 && (
          <CostCentreGroup
            title="Other categories"
            subtitle="Not tagged S&M. Tick to fold into fully-loaded CAC."
            components={otherComponents}
            config={config}
            colorByKey={colorByKey}
            onIncluded={setIncluded}
            onProration={setProration}
          />
        )}
      </div>

      {/* Charts */}
      <KpiChart
        // Remount when the included set changes so echarts drops removed series
        // instead of merging stale bars (default merge keeps toggled-off series).
        // JSON-encode rather than join: cost-centre keys are category cxids that
        // can contain spaces, commas or " > ", which would let a plain separator
        // collide across different included sets.
        key={JSON.stringify(includedKeys)}
        title="Acquisition spend by cost centre (prorated)"
        series={
          spendSeries.length > 0
            ? spendSeries
            : [
                {
                  name: "—",
                  type: "bar",
                  points: months.map((m) => ({ month: m, value: 0 })),
                },
              ]
        }
        format="money"
        onMonthClick={setSelectedMonth}
      />
      <BasisDivergenceNote />
      <KpiChart
        title="CAC over time (bar = month, line = TTM)"
        series={[
          {
            name: "Monthly CAC",
            type: "bar",
            color: "#94a3b8",
            points: months.map((m, i) => ({ month: m, value: monthlyCac[i] })),
          },
          {
            name: "TTM CAC",
            type: "line",
            color: "#111827",
            points: months.map((m, i) => ({ month: m, value: ttmCac[i] })),
          },
        ]}
        format="money"
        onMonthClick={() => {}}
      />
      <KpiChart
        title="New customers per month"
        series={[
          {
            name: "New customers",
            type: "bar",
            color: "#16a34a",
            points: months.map((m, i) => ({ month: m, value: customers[i] })),
          },
        ]}
        format="number"
        onMonthClick={() => {}}
      />

      <DataHealth components={shownComponents} months={months} />

      {selectedMonth && (
        <CacMonthPanel
          month={selectedMonth}
          includedKeys={includedKeys}
          colorByKey={colorByKey}
          onClose={() => setSelectedMonth(null)}
        />
      )}
    </div>
  );
}

function CostCentreGroup({
  title,
  subtitle,
  components,
  config,
  colorByKey,
  onIncluded,
  onProration,
}: {
  title: string;
  subtitle: string;
  components: readonly Component[];
  config: Record<string, { included: boolean; proration: number }>;
  colorByKey: Record<string, string>;
  onIncluded: (key: string, included: boolean) => void;
  onProration: (key: string, percent: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      {components.map((component) => {
        const unmapped = component.categoryCount === 0;
        return (
          <div key={component.key} className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 w-64">
              <input
                type="checkbox"
                checked={config[component.key].included}
                onChange={(e) => onIncluded(component.key, e.target.checked)}
              />
              <span
                className="text-sm"
                style={{ color: colorByKey[component.key] }}
              >
                {component.label}
              </span>
              {!component.defaultIncluded && component.pnlLine && (
                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                  {component.pnlLine.replace(/_/g, " ")}
                </span>
              )}
              {unmapped && (
                <span className="text-xs text-red-600">no categories</span>
              )}
            </label>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <input
                type="number"
                min={0}
                max={100}
                value={Math.round(config[component.key].proration * 100)}
                onChange={(e) =>
                  onProration(component.key, Number(e.target.value))
                }
                disabled={!config[component.key].included}
                className="w-16 px-2 py-1 border rounded text-right tabular-nums disabled:opacity-40"
              />
              <span>% included</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({
  label,
  value,
  primary,
}: {
  label: string;
  value: string;
  primary?: boolean;
}) {
  return (
    <div className="bg-white rounded border p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={primary ? "text-2xl font-bold" : "text-lg font-semibold"}>
        {value}
      </div>
    </div>
  );
}

function DataHealth({
  components,
  months,
}: {
  components: readonly Component[];
  months: readonly string[];
}) {
  return (
    <div className="space-y-3 bg-white rounded border p-4">
      <div>
        <h2 className="font-semibold">Data health</h2>
        <p className="text-sm text-gray-500">
          One cell per cost-centre month.{" "}
          <span className="inline-block w-3 h-3 bg-green-200 rounded-sm align-middle" />{" "}
          spend present ·{" "}
          <span className="inline-block w-3 h-3 bg-red-400 rounded-sm align-middle" />{" "}
          gap (no spend in a month surrounded by spend — likely missing
          categorization) ·{" "}
          <span className="inline-block w-3 h-3 bg-gray-100 border rounded-sm align-middle" />{" "}
          outside the cost centre's active span
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="text-xs border-separate" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="text-left pr-3 sticky left-0 bg-white">
                Cost centre
              </th>
              {months.map((month) => (
                <th
                  key={month}
                  className="font-normal text-gray-400"
                  style={{ writingMode: "vertical-rl", fontSize: 9 }}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {components.map((component) => {
              const spendByMonth = new Map(
                component.monthly.map((p) => [p.month, p.spend])
              );
              const present = months.filter(
                (m) => (spendByMonth.get(m) ?? 0) > 0
              );
              const firstActive = present[0];
              const lastActive = present[present.length - 1];

              return (
                <tr key={component.key}>
                  <td className="pr-3 whitespace-nowrap sticky left-0 bg-white">
                    <span className="font-medium">{component.label}</span>
                  </td>
                  {months.map((month) => {
                    const spend = spendByMonth.get(month) ?? 0;
                    const inSpan =
                      firstActive != null &&
                      month >= firstActive &&
                      month <= lastActive;
                    const cellClass =
                      spend > 0
                        ? "bg-green-200"
                        : inSpan
                        ? "bg-red-400"
                        : "bg-gray-100";
                    return (
                      <td
                        key={month}
                        title={`${component.label} ${month}: ${money(spend)}`}
                        className={`${cellClass} rounded-sm`}
                        style={{ width: 12, height: 16 }}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-sm text-gray-600 space-y-1">
        {components.map((c) => {
          const spendByMonth = new Map(
            c.monthly.map((p) => [p.month, p.spend])
          );
          const present = months.filter((m) => (spendByMonth.get(m) ?? 0) > 0);
          const first = present[0];
          const last = present[present.length - 1];
          const gaps =
            first == null
              ? []
              : months.filter(
                  (m) =>
                    m >= first &&
                    m <= last &&
                    (spendByMonth.get(m) ?? 0) === 0
                );
          if (gaps.length === 0) return null;
          return (
            <p key={c.key}>
              <span className="font-medium">{c.label}</span> has no spend in{" "}
              {gaps.length} month
              {gaps.length > 1 ? "s" : ""}: {gaps.join(", ")} — categorize that
              month's transactions into the relevant categories.
            </p>
          );
        })}
      </div>
    </div>
  );
}
